import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { filter, takeUntil } from 'rxjs';
import { CommonModule, NgClass } from '@angular/common';
import { SplashScreenService } from '../core/splash/splash-screen.service';
import { FooterComponent } from './components/footer/footer.component';
import { PartnersCarouselComponent } from './components/partners-carousel/partners-carousel.component';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { WorkOfArtModalComponent } from './modals/work-of-art-modal/work-of-art-modal.component';
import { Location } from '@angular/common';
import { register } from 'swiper/element/bundle';
import { AnalyticsService } from '../core/analytics/analytics.service';
import { PopupService } from '../core/popup/popup.service';
import { SeasonalPopupComponent } from './modals/seasonal-popup/seasonal-popup.component';
import { DownloadAppPromptComponent } from './modals/download-app-prompt/download-app-prompt.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    PartnersCarouselComponent,
    FooterComponent,
    DialogModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements AfterViewInit {
  viewHeader = false;
  dialog = inject(Dialog);
  public isModalOpen = false; // nos ayudará a no abrir dos modales simultáneamente

  constructor(
    private router: Router,
    private splash: SplashScreenService, // private _cdr: ChangeDetectorRef
    private route: ActivatedRoute,
    private location: Location,
    private analytics: AnalyticsService,
    private popupService: PopupService,
  ) {
    this.splash.show();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        // Si la URL empieza con /maintenance, /postulation o /timer, ocultar header
        if (
          e.urlAfterRedirects.startsWith('/maintenance') ||
          e.urlAfterRedirects.startsWith('/postulation') ||
          e.urlAfterRedirects.startsWith('/timer')
        ) {
          this.viewHeader = false;
        } else {
          this.viewHeader = true;
        }

        // this.viewHeader = e.urlAfterRedirects.startsWith('/maintenance');
      });

    // 2) Si la app arranca con ?workOfArtId=XYZ en la URL, abrimos el modal al cargar
    this.route.queryParamMap.pipe().subscribe((qpMap) => {
      const id = qpMap.get('workOfArtId');

      if (id && !this.isModalOpen) {
        // Si recibo un workOfArtId en la URL y no hay modal abierto, abro el modal.
        this.openWorkOfArtModal({ _id: id });
      }
      // Si id es null, no hago nada: el modal precisamente se cierra desde AppComponent
    });

    // time 2 second
    setTimeout(() => {
      this.splash.hide();
    }, 2000);

    this.bootstrapSeasonalPopup();
  }

  ngAfterViewInit(): void {
    // Register Swiper custom elements
    register();
  }

  /**
   * Pop-up estacional + captación a la app (frente #2).
   * Aparece en rutas públicas estándar tras splash.
   *
   * El prompt built-in tiene prioridad en toda visita pública elegible, incluso
   * desde `?src=qr`, y comparte un cooldown de 24h. Mientras ese cooldown está
   * vigente se evalúa el popup estacional normal. Así nunca se apilan diálogos.
   *
   * `?src=qr` es un param independiente y NO gatilla el flujo de workOfArtId.
   * Guards: no auth, no maintenance/postulation/timer, no workOfArtId, no modal
   * abierto.
   */
  private bootstrapSeasonalPopup(): void {
    setTimeout(() => {
      // Guard 1: si estamos en flujo de auth, NO mostrar popup encima del login/signup
      if (this.router.url.startsWith('/auth')) return;

      // Guard 2: si ya hay un modal abierto (ej. WorkOfArtModal por queryParam),
      // o no estamos en una ruta pública estándar, no mostrar.
      if (this.isModalOpen || !this.viewHeader) return;

      // Guard 3: tampoco mostrar si la URL trae workOfArtId
      if (this.route.snapshot.queryParamMap.get('workOfArtId')) return;

      // Guard 4: /app ES la página de descarga (destino del QR del concurso).
      // Invitar a descargar encima de ella tapa lo que la persona vino a hacer
      // y quema el cooldown de 24h que sí sirve en cualquier otra visita.
      if (this.enLaPaginaDeDescarga()) return;

      // El prompt built-in gana en toda visita elegible. Al abrir retornamos,
      // por lo que no puede coexistir con el popup estacional.
      if (!this.downloadPromptRecentlyShown()) {
        this.openDownloadAppPrompt();
        return;
      }

      // Durante el cooldown del prompt, el flujo estacional sigue disponible.
      this.popupService.getActive().subscribe((popup) => {
        if (!popup) return;
        if (this.popupService.wasRecentlyShown(popup)) return;

        // Re-chequear TODOS los guards en el momento de abrir
        // (el usuario puede haber navegado durante el await)
        if (this.router.url.startsWith('/auth')) return;
        if (this.isModalOpen || !this.viewHeader) return;

        this.isModalOpen = true;
        const dialogRef = this.dialog.open(SeasonalPopupComponent, {
          data: popup,
          panelClass: 'seasonal-popup-panel',
          hasBackdrop: true,
          backdropClass: 'seasonal-popup-backdrop',
        });

        dialogRef.closed.subscribe(() => {
          this.popupService.markShown(popup);
          this.isModalOpen = false;
        });
      });
    }, 2500);
  }

  /**
   * ¿Estamos en `/app`? Compara la ruta sin query ni fragmento, para no
   * confundir una ruta futura que empiece con las mismas letras.
   */
  private enLaPaginaDeDescarga(): boolean {
    const ruta = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
    return ruta === '/app';
  }

  /** Cooldown del prompt built-in de descarga (independiente del estacional). */
  private static readonly DL_PROMPT_KEY = 'artstgo_dl_app_prompt_seen_at';
  private static readonly DL_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h

  /**
   * Abre el prompt BUILT-IN de descarga de la app. Marca el cooldown al abrir
   * (no al cerrar) para que un re-scan inmediato no lo vuelva a mostrar.
   */
  private openDownloadAppPrompt(): void {
    this.isModalOpen = true;
    this.markDownloadPromptShown();
    const dialogRef = this.dialog.open(DownloadAppPromptComponent, {
      panelClass: 'download-app-prompt-panel',
      hasBackdrop: true,
      backdropClass: 'download-app-prompt-backdrop',
      autoFocus: true,
      restoreFocus: true,
    });
    dialogRef.closed.subscribe(() => {
      this.isModalOpen = false;
    });
  }

  private downloadPromptRecentlyShown(): boolean {
    try {
      const seenAt = localStorage.getItem(AppComponent.DL_PROMPT_KEY);
      if (!seenAt) return false;
      return (
        Date.now() - new Date(seenAt).getTime() <
        AppComponent.DL_PROMPT_COOLDOWN_MS
      );
    } catch {
      // Safari ITP estricto / incógnito puede lanzar al leer localStorage.
      // Degradación aceptable: tratar como "no visto" → el prompt aparece.
      return false;
    }
  }

  private markDownloadPromptShown(): void {
    try {
      localStorage.setItem(
        AppComponent.DL_PROMPT_KEY,
        new Date().toISOString(),
      );
    } catch {
      // Silenciar — el cooldown no persiste pero la app no crashea.
    }
  }

  /**
   * Este método se encarga de:
   *  1) Inyectar `?workOfArtId=<id>` en la URL
   *  2) Abrir el modal
   *  3) Al cerrarse,
   *     • Si se cierra normalmente (x === null/undefined) ⇒ limpiar el queryParam
   *     • Si `x` viene con un objeto nuevo ⇒ primero actualizar la URL con el nuevo ID y luego reabrir modal
   */
  openWorkOfArtModal(workOfArt: { _id: string }) {
    // 1) Si ya hay un modal abierto, salimos
    if (this.isModalOpen) return;
    this.isModalOpen = true;

    // 2) Actualizamos la URL para que quede ?workOfArtId=<id> (merge para no borrar otros params)
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { workOfArtId: workOfArt._id },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then((xy) => {
        // 3) Abrimos el diálogo con ese workOfArt
        const dialogRef = this.dialog.open(WorkOfArtModalComponent, {
          maxHeight: '90%',
          maxWidth: '90vw',
          data: { ...workOfArt },
          disableClose: true, // Evita que se cierre al hacer click fuera del modal
        });

        // Track modal open
        this.analytics.trackModalOpen('work_of_art_modal');
        this.analytics.trackWorkOfArtView(workOfArt._id);

        dialogRef.closed.subscribe((x: any) => {
          // Track modal close
          this.analytics.trackModalClose('work_of_art_modal');

          // 3) Al cerrarse el
          // Si x existe, significa que dentro del modal se disparó algo así como `close(x)`
          // y x trae la obra nueva que queremos abrir. En ese caso:
          if (x) {
            // 3a) Marcamos isModalOpen = false para permitir abrir de nuevo
            this.isModalOpen = false;
            // 3b) Primero actualizamos la URL con el NUEVO ID de obra
            this.router
              .navigate([], {
                relativeTo: this.route,
                queryParams: { workOfArtId: x._id },
                queryParamsHandling: 'merge',
                replaceUrl: true,
              })
              .then(() => {
                // 3c) Reabrimos el modal con la nueva obra
                this.openWorkOfArtModal({ _id: x._id });
              });
            return;
          }
          this.isModalOpen = false;

          // 3d) Si x es null/undefined (cerró sin solicitar otra obra), limpiamos la URL
          // Reemplazamos el estado del history por la misma ruta sin ese parámetro
          // const tree = this.router.createUrlTree([], {
          //   relativeTo: this.route,
          //   queryParams: { workOfArtId: null },
          //   queryParamsHandling: 'merge',
          // });
          // this.location.replaceState(tree.toString());
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { workOfArtId: null }, // Limpiamos el ID del modal
            queryParamsHandling: 'merge',
            replaceUrl: true, // Reemplazamos la URL sin agregar al historial
          });

          // 3e) Ya no hay modal abierto
        });
      });
  }
}
